// Live WhatsApp-style preview of a message template:
// realistic smartphone mockup with green WhatsApp header, incoming message bubble
// with header media / text, body with {{n}} variables, footer, action buttons,
// and horizontal scrollable carousel cards.

import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { replaceVariablesWithExamples, splitTemplateBody } from '../../lib/templateText';
import type {
  TplButton,
  TplButtonType,
  TplCarouselCard,
  TplHeaderType,
} from '../../lib/templateTypes';

const BTN_ICON: Record<TplButtonType, string> = {
  URL: '↗',
  PHONE_NUMBER: '📞',
  QUICK_REPLY: '↩',
  COPY_CODE: '📋',
  OTP: '🔑',
};

export interface TemplatePreviewProps {
  headerType?: TplHeaderType;
  headerText?: string;
  headerMediaUrl?: string | null;
  headerMediaMime?: string | null;
  headerMediaName?: string | null;
  body: string;
  footer?: string;
  buttons?: TplButton[];
  variables?: Array<{ index: number; example: string }>;
  carouselCards?: TplCarouselCard[];
  businessName?: string;
  className?: string;
}

export default function TemplatePreview({
  headerType = 'NONE',
  headerText = '',
  headerMediaUrl,
  headerMediaMime,
  headerMediaName,
  body,
  footer,
  buttons = [],
  variables = [],
  carouselCards = [],
  businessName = 'WaTag Marketing',
  className = '',
}: TemplatePreviewProps) {
  const { t } = useI18n();
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Substitute variables with examples if available for a realistic preview
  const resolvedHeaderText = replaceVariablesWithExamples(headerText, variables);
  const resolvedBodyText = replaceVariablesWithExamples(body, variables);
  const bodyParts = splitTemplateBody(body);

  const hasCarousel = carouselCards.length > 0;

  return (
    <div
      className={`mx-auto w-full max-w-[340px] overflow-hidden rounded-[24px] border-4 border-slate-700 bg-slate-900 shadow-2xl ${className}`}
    >
      {/* Phone status bar */}
      <div className="flex items-center justify-between bg-chat-header px-4 pt-2 text-[10px] font-semibold text-white/90">
        <span>9:41</span>
        <div className="flex items-center gap-1.5">
          <span>5G</span>
          <span>100%</span>
        </div>
      </div>

      {/* WhatsApp Chat Header */}
      <div className="flex items-center gap-2.5 bg-chat-header px-3 py-2.5 text-white">
        <span className="text-xs">←</span>
        <div className="grid h-8 w-8 place-items-center rounded-full bg-white/20 text-xs font-bold uppercase">
          {businessName.slice(0, 2)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold leading-tight">{businessName}</div>
          <div className="flex items-center gap-1 text-[10px] text-green-200">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
            <span>{t('tpl.preview.online') || 'Business Account'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/80">
          <span className="text-xs">⋮</span>
        </div>
      </div>

      {/* WhatsApp Message Area */}
      <div className="min-h-[380px] bg-chat-bg p-3">
        {/* Date pill */}
        <div className="mb-3 flex justify-center">
          <span className="rounded-md bg-white/80 px-2 py-0.5 text-[10px] font-medium text-slate-600 shadow-sm">
            {t('tpl.preview.today') || 'Today'}
          </span>
        </div>

        {/* Main Message Bubble */}
        <div className="max-w-[280px] overflow-hidden rounded-2xl rounded-tl-none bg-white shadow-sm">
          {/* Header Component */}
          {headerType === 'TEXT' && resolvedHeaderText.trim() && (
            <div className="border-b border-slate-100 px-3 pt-2.5 pb-1 font-bold text-slate-900">
              <p className="text-xs">{resolvedHeaderText}</p>
            </div>
          )}

          {headerType === 'IMAGE' && (
            <div className="relative aspect-video w-full bg-slate-100">
              {headerMediaUrl ? (
                <img
                  src={headerMediaUrl}
                  alt="Template header"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <span className="text-2xl">🖼️</span>
                  <span className="mt-1 text-[11px] font-medium">
                    {t('tpl.header.image') || 'Header Image'}
                  </span>
                </div>
              )}
            </div>
          )}

          {headerType === 'VIDEO' && (
            <div className="relative aspect-video w-full bg-slate-800">
              <div className="flex h-full flex-col items-center justify-center text-white/80">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-white/20 text-lg">
                  ▶
                </span>
                <span className="mt-1 text-[11px] font-medium">
                  {t('tpl.header.video') || 'Header Video'}
                </span>
              </div>
            </div>
          )}

          {headerType === 'DOCUMENT' && (
            <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 p-2.5">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-red-100 text-base text-red-600 font-bold">
                PDF
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-slate-800">
                  {headerMediaName || 'Document_Sample.pdf'}
                </div>
                <div className="text-[10px] text-slate-400">
                  {headerMediaMime || 'application/pdf'}
                </div>
              </div>
            </div>
          )}

          {headerType === 'LOCATION' && (
            <div className="relative aspect-[2/1] w-full bg-emerald-50 p-2">
              <div className="flex h-full flex-col items-center justify-center text-emerald-700">
                <span className="text-xl">📍</span>
                <span className="text-[11px] font-semibold">Location Shared</span>
              </div>
            </div>
          )}

          {/* Body Component */}
          <div className="p-3">
            <div className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-800">
              {body.trim() ? (
                variables.length > 0 && resolvedBodyText !== body ? (
                  <span>{resolvedBodyText}</span>
                ) : (
                  bodyParts.map((p, i) =>
                    p.variable ? (
                      <span
                        key={i}
                        className="rounded bg-indigo-50 px-1 font-semibold text-indigo-600 border border-indigo-200 inline-block text-xs"
                      >
                        {p.text}
                      </span>
                    ) : (
                      <span key={i}>{p.text}</span>
                    ),
                  )
                )
              ) : (
                <span className="italic text-slate-400">{t('tpl.preview.empty') || 'Message body...'}</span>
              )}
            </div>

            {/* Footer Component */}
            {footer && footer.trim() && (
              <p className="mt-1.5 text-[11px] text-slate-400">{footer}</p>
            )}

            {/* Timestamp and ticks */}
            <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-400">
              <span>12:30 PM</span>
              <span className="text-blue-500 font-bold">✓✓</span>
            </div>
          </div>
        </div>

        {/* Action Buttons (Attached below the bubble) */}
        {buttons.length > 0 && !hasCarousel && (
          <div className="mt-1 max-w-[280px] overflow-hidden rounded-xl bg-white shadow-sm divide-y divide-slate-100">
            {buttons.map((b, i) => (
              <div
                key={i}
                className="flex items-center justify-center gap-2 py-2 px-3 text-center text-xs font-semibold text-blue-600 hover:bg-slate-50 cursor-pointer"
              >
                <span className="text-xs" aria-hidden>
                  {BTN_ICON[b.type] || '↗'}
                </span>
                <span className="truncate">{b.text || t(`tpl.btn.${b.type}`) || b.type}</span>
              </div>
            ))}
          </div>
        )}

        {/* Carousel Cards Preview */}
        {hasCarousel && (
          <div className="mt-2.5">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory">
              {carouselCards.map((card, idx) => (
                <div
                  key={card.id || idx}
                  className="w-[200px] shrink-0 snap-start overflow-hidden rounded-xl bg-white shadow-sm"
                  onClick={() => setActiveCardIndex(idx)}
                >
                  {/* Card Media */}
                  <div className="relative aspect-video w-full bg-slate-100">
                    <div className="flex h-full flex-col items-center justify-center text-slate-400">
                      <span className="text-lg">
                        {card.mediaType === 'VIDEO' ? '🎥' : '🖼️'}
                      </span>
                      <span className="text-[10px] font-medium">Card {idx + 1}</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-2.5">
                    <p className="line-clamp-3 text-xs leading-snug text-slate-800">
                      {card.bodyText || `Card body text...`}
                    </p>
                  </div>

                  {/* Card Buttons */}
                  {card.buttons && card.buttons.length > 0 && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {card.buttons.map((btn, bIdx) => (
                        <div
                          key={bIdx}
                          className="flex items-center justify-center gap-1 py-1.5 text-center text-[11px] font-semibold text-blue-600"
                        >
                          <span aria-hidden>{BTN_ICON[btn.type] || '↗'}</span>
                          <span className="truncate">{btn.text || 'Button'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Carousel Pagination Dots */}
            {carouselCards.length > 1 && (
              <div className="mt-1 flex justify-center gap-1">
                {carouselCards.map((_, dotIdx) => (
                  <button
                    key={dotIdx}
                    type="button"
                    onClick={() => setActiveCardIndex(dotIdx)}
                    className={`h-1.5 rounded-full transition-all ${
                      activeCardIndex === dotIdx ? 'w-4 bg-emerald-600' : 'w-1.5 bg-slate-300'
                    }`}
                    aria-label={`Go to card ${dotIdx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
